<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import { Overlay } from '@collector/shared-ui'
import { Action, ActionStatus } from '@mobile/ActionQueue/Action'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

const REDISPLAY_DELAY_MS = 60_000

const { actionQueue } = useEventInjections()

const dismissedErrorIds = ref<Set<string>>(new Set())
let redisplayTimeout: ReturnType<typeof setTimeout> | null = null

const backdropRef = ref<HTMLElement | null>(null)
const messageTopPx = ref<number | null>(null)

const erroredActions = computed(() =>
  actionQueue.value.queue.filter(
    (action): action is Action<unknown> =>
      action.status === ActionStatus.Errored,
  ),
)

const visible = computed(() => {
  if (import.meta.env.VITE_APP_MODE === 'e2e') return false
  if (erroredActions.value.length === 0) return false

  return erroredActions.value.some(
    (action) => !dismissedErrorIds.value.has(action.id),
  )
})

watch(
  () => erroredActions.value.length,
  (length) => {
    if (length === 0) {
      dismissedErrorIds.value = new Set()
      clearRedisplayTimeout()
    }
  },
)

function updateMessagePosition(): void {
  if (!backdropRef.value) {
    messageTopPx.value = null
    return
  }
  const rect = backdropRef.value.getBoundingClientRect()
  const visibleTop = Math.max(0, rect.top)
  const visibleBottom = Math.min(window.innerHeight, rect.bottom)

  if (visibleBottom <= visibleTop) {
    messageTopPx.value = null
    return
  }

  messageTopPx.value = (visibleTop + visibleBottom) / 2
}

watch(visible, () => {
  nextTick(updateMessagePosition)
})

function clearRedisplayTimeout(): void {
  if (redisplayTimeout !== null) {
    clearTimeout(redisplayTimeout)
    redisplayTimeout = null
  }
}

function dismiss(): void {
  dismissedErrorIds.value = new Set(erroredActions.value.map((a) => a.id))

  clearRedisplayTimeout()
  redisplayTimeout = setTimeout(() => {
    if (erroredActions.value.length > 0) dismissedErrorIds.value = new Set()
  }, REDISPLAY_DELAY_MS)
}

onMounted(() => {
  window.addEventListener('scroll', updateMessagePosition)
  updateMessagePosition()
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateMessagePosition)
  clearRedisplayTimeout()
})
</script>

<template>
  <Overlay :modelValue="visible">
    <div
      ref="backdropRef"
      class="absolute inset-0 z-50 h-full w-full bg-white/50 backdrop-blur"
    />
    <div
      v-if="messageTopPx !== null"
      class="fixed left-1/2 z-50 -translate-x-1/2 -translate-y-1/2"
      :style="{ top: `${messageTopPx}px` }"
    >
      <div class="relative rounded-lg bg-white px-8 py-6 text-center shadow-lg">
        <button
          class="bg-red absolute -top-3 -right-3 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white shadow"
          @click="dismiss"
        >
          <FontAwesomeIcon icon="times" />
        </button>
        <span class="text-xl font-bold">
          Queue is blocked.
          <br />
          Resolve this issue to continue relation.
        </span>
      </div>
    </div>
  </Overlay>
</template>
